/**
 * ÇEREZ ONAYININ TEK KAYNAĞI — kategoriler, saklama biçimi ve okuma.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 "Bu çerezi basabilir miyim?" sorusunun cevabı BURADAN okunur
 * ═══════════════════════════════════════════════════════════════════════════
 * Şerit (`components/cookie-consent.tsx`) yalnızca bu dosyadaki kararı SORAN
 * yüzey. Kararın kendisi, nerede durduğu ve nasıl okunduğu burada. İleride
 * bir analitik betiği eklenecekse koşulu kendi dosyasında yeniden yazmaz —
 * `consentAllows(readConsent(), 'analytics')` der.
 *
 * ---------------------------------------------------------------------------
 * Neden `localStorage` değil ÇEREZ
 * ---------------------------------------------------------------------------
 * Üç sebep, üçü de teknik:
 *
 *   1. Sunucu da okuyabilsin. `localStorage` yalnızca tarayıcıda; sunucu
 *      bileşeni bir betiği hiç basmamayı seçecekse kararı ilk isteğin
 *      başlığında görmek zorunda (`cookies().get(CONSENT_COOKIE)`).
 *   2. Süre kendiliğinden dolsun. `Max-Age` bir yıl sonra kaydı siler ve
 *      şerit yeniden çıkar — onayın süresiz olmaması gereken bir şey olduğu
 *      için bu istenen davranış, kusur değil.
 *   3. Kararın kendisi zaten "çerez kabul ediyorum" demek. Onayı zorunlu bir
 *      çerezde tutmak tutarlı; onay kaydını ayrı bir depoda saklamak değil.
 *
 * `httpOnly` BİLEREK yok: şeridin kendisi kararı okumak zorunda.
 *
 * ---------------------------------------------------------------------------
 * Sürüm (`CONSENT_VERSION`)
 * ---------------------------------------------------------------------------
 * Kategori listesi değişirse — yeni bir kategori eklenir ya da mevcut birinin
 * kapsamı genişlerse — eski onay artık o soruya verilmiş bir cevap değil.
 * Sürüm bir artırılır, kayıtlı karar geçersiz sayılır ve şerit herkese
 * yeniden çıkar. Sürüm artırmadan kategori eklemek, kullanıcının hiç
 * görmediği bir şeye onay vermiş sayılması demek.
 */

/** Zorunlu çerezler dışındaki her şeyin bağlı olduğu kayıt. */
export const CONSENT_COOKIE = 'mentorivo_consent';

/** Kategori listesi değişince ARTIR — gerekçe dosya başlığında. */
export const CONSENT_VERSION = 1;

/** 12 ay. Dolunca karar silinir ve şerit yeniden sorar. */
export const CONSENT_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Üç kategori, dördüncüsü yok.
 *
 * - `necessary`  — oturum, workspace seçimi, güvenlik. Kapatılamaz: kapatmak
 *                  siteyi çalışmaz hâle getirir, yani sorulacak bir şey değil.
 * - `analytics`  — ziyaretin ölçülmesi.
 * - `marketing`  — reklam ölçümü ve yeniden hedefleme.
 */
export const CONSENT_CATEGORIES = ['necessary', 'analytics', 'marketing'] as const;
export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

/** Kullanıcının gerçekten seçebildikleri — `necessary` kilitli olduğu için dışarıda. */
export const OPTIONAL_CATEGORIES = ['analytics', 'marketing'] as const;
export type OptionalCategory = (typeof OPTIONAL_CATEGORIES)[number];

/** Panelin açık/kapalı durumu; kaydedilmeden önceki hâli. */
export type ConsentDraft = Record<OptionalCategory, boolean>;

/** Çerezde duran kayıt. */
export type ConsentChoice = ConsentDraft & {
  version: number;
  /** ISO 8601. Kararın ne zaman verildiği — KVKK/GDPR'da onayın ispatı. */
  decidedAt: string;
};

/**
 * Footer'daki "Çerez tercihleri" girişinin şeridi yeniden açmak için attığı
 * olay. Doğrudan bir prop geçilemiyor: footer sayfanın içinde, şerit gövdenin
 * sonundaki portalda ve ikisinin ortak bir atası yok.
 */
export const CONSENT_PREFERENCES_EVENT = 'mentorivo:cookie-preferences';

/**
 * Karar her değiştiğinde atılır. Şu an dinleyeni yok; ileride bir betik
 * yükleyicisi eklendiğinde kancası bu — sayfa yenilenmeden de kapanabilmeli.
 */
export const CONSENT_CHANGE_EVENT = 'mentorivo:cookie-consent';

/**
 * Ham çerez değerini karara çevirir. Saf: hem tarayıcıda hem sunucu
 * bileşeninde çağrılabiliyor.
 *
 * Okunamayan, bozuk ya da ESKİ SÜRÜM bir kayıt `null` dönüyor — üçü de aynı
 * şey demek: "bu kullanıcının geçerli bir kararı yok, sor".
 */
export function parseConsent(raw: string | null | undefined): ConsentChoice | null {
  if (!raw) return null;

  let value: unknown;
  try {
    value = JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }

  if (typeof value !== 'object' || value === null) return null;

  const record = value as Record<string, unknown>;
  if (record.version !== CONSENT_VERSION) return null;
  if (typeof record.analytics !== 'boolean' || typeof record.marketing !== 'boolean') return null;

  return {
    version: CONSENT_VERSION,
    analytics: record.analytics,
    marketing: record.marketing,
    decidedAt: typeof record.decidedAt === 'string' ? record.decidedAt : '',
  };
}

/** `document.cookie` içinden ham değeri çeker. Yalnızca tarayıcıda. */
function rawCookie(): string | null {
  if (typeof document === 'undefined') return null;

  for (const part of document.cookie.split(';')) {
    const entry = part.trim();
    if (entry.startsWith(`${CONSENT_COOKIE}=`)) {
      return entry.slice(CONSENT_COOKIE.length + 1);
    }
  }
  return null;
}

/** Tarayıcıdaki geçerli karar; yoksa `null`. */
export function readConsent(): ConsentChoice | null {
  return parseConsent(rawCookie());
}

/**
 * Kararı yazar ve dinleyenlere haber verir.
 *
 * `Secure` yalnız https'te: yerelde (http://localhost:3041) `Secure` bayrağı
 * taşıyan çerez tarayıcı tarafından sessizce atılıyor ve karar hiç
 * kaydedilmemiş gibi oluyordu.
 */
export function saveConsent(draft: ConsentDraft): ConsentChoice {
  const choice: ConsentChoice = {
    version: CONSENT_VERSION,
    analytics: draft.analytics,
    marketing: draft.marketing,
    decidedAt: new Date().toISOString(),
  };

  if (typeof document !== 'undefined') {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const value = encodeURIComponent(JSON.stringify(choice));
    document.cookie = `${CONSENT_COOKIE}=${value}; Path=/; Max-Age=${CONSENT_MAX_AGE}; SameSite=Lax${secure}`;
    window.dispatchEvent(new CustomEvent<ConsentChoice>(CONSENT_CHANGE_EVENT, { detail: choice }));
  }

  return choice;
}

/** Kararı siler — şerit bir sonraki ziyarette yeniden sorar. */
export function clearConsent(): void {
  if (typeof document === 'undefined') return;

  document.cookie = `${CONSENT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
  window.dispatchEvent(new CustomEvent<null>(CONSENT_CHANGE_EVENT, { detail: null }));
}

/**
 * Bir kategori açık mı?
 *
 * `necessary` her zaman açık — karar yokken bile. Kilitli olmasının kod
 * karşılığı bu satır: çağıran yerde "ama bu zorunluydu" istisnası yazılmasın.
 */
export function consentAllows(choice: ConsentChoice | null, category: ConsentCategory): boolean {
  if (category === 'necessary') return true;
  if (!choice) return false;
  return choice[category];
}

/** Footer girişinin çağırdığı tek şey: şeridi ayarlar açık hâlde göster. */
export function openCookiePreferences(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(CONSENT_PREFERENCES_EVENT));
}
